<template>
    <div class="relative">
        <nav
            id="navbar"
            class="flex fixed w-full items-center justify-between px-6 h-16 bg-gray-100 text-gray-700 border-b border-gray-200 z-50">
            <div class="flex items-center">
                <button aria-label="Open Menu" class="mr-2" @click="drawer">
                    <svg
                        class="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                    >
                        <path d="M4 6h16M4 12h16M4 18h16"></path>
                    </svg>
                </button>
                <a href="/" class="hidden md:block">
                    <img alt="Platform Light" class="h-auto w-40 mx-auto" src="/assets/logo.png"/>
                    <span class="ml-8"><strong>POS ADMIN</strong></span>
                </a>
            </div>
            <div class="flex items-center">
                <div class="flex justify-between bg-transparent">
                    <switch-language
                        v-if="!$page.props.impersonator"
                        class="mr-6 mt-1 hidden sm:block"
                    />
                    <div
                        v-if="$page.props.impersonator"
                        class="flex items-center px-3 py-1 font-medium ml-2"
                        :class="{
                            'bg-danger': $page.props.impersonator,
                            'text-white': $page.props.impersonator
                        }"
                    >
                        <span class="text font-semibold pr-1">{{ $page.props.impersonator }}</span>
                        <span class="text pr-1">{{ $t('impersonated as') }}</span>
                        <span class="text font-semibold" dusk="logged-user">{{ $page.props.user.username }}</span>
                    </div>
                    <inertia-link
                        v-else
                        :href="route('user.changePassword')"
                        class="flex items-center px-3 py-1 font-medium ml-2 hover:text-purple-700 focus:text-purple-700"
                        :class="{
                            'bg-danger': $page.props.impersonator,
                            'text-white': $page.props.impersonator
                        }"
                        :title="$t('Change Password')">
                        <font-awesome-icon icon="user" class="mr-2"/>
                        <span class="text font-semibold" dusk="logged-user">{{ $page.props.user.username }}</span>
                    </inertia-link>
                    <button
                        @click="logout"
                        class="hidden sm:flex items-center px-3 py-1 font-semibold ml-6 text-center rounded"
                        :title="$t('Logout')"
                        dusk="logout-button">
                        <font-awesome-icon icon="power-off"/>
                    </button>
                </div>
            </div>

            <transition
                enter-active-class="ease-out transition-medium"
                enter-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="ease-out transition-medium"
                leave-class="opacity-100"
                leave-to-class="opacity-0">
                <div v-show="isOpen" class="z-10 fixed inset-0 transition-opacity" @keydown.esc="isOpen = false">
                    <div class="absolute inset-0 bg-black opacity-50" tabindex="0" @click="isOpen = false"></div>
                </div>
            </transition>
            <aside
                :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
                class="transform top-0 left-0 w-64 bg-gray-100 fixed h-full overflow-auto ease-in-out transition-all duration-300 z-30">
                <a class="flex w-full items-center p-4 border-b" @click="isOpen = false">
                    <img alt="Platform Light" class="h-auto w-40" src="/assets/logo.png"/>
                </a>
                <inertia-link
                    :href="route('pos.home')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('pos.home') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="home" />
                    </span>
                    <span>{{ $t('Home') }}</span>
                </inertia-link>

                <inertia-link
                    :href="route('players.index')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('players.index', 'players.show', 'players.deposit', 'withdrawals.show') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="users" />
                    </span>
                    <span>{{ $t('Players') }}</span>
                </inertia-link>

<!--                <inertia-link-->
<!--                    :href="route('pos.games')"-->
<!--                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"-->
<!--                    :class="isRoute('pos.games') ? 'text-white bg-purple-600' : ''"-->
<!--                    @click="isOpen = false">-->
<!--                    <span class="mr-2 w-icon">-->
<!--                        <font-awesome-icon icon="dice-d6" />-->
<!--                    </span>-->
<!--                    <span>{{ $t('Games') }}</span>-->
<!--                </inertia-link>-->

                <inertia-link
                    v-if="withdrawalPlayerWithPinAllowed"
                    :href="route('withdrawals.index')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('withdrawals.index') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="money-check" />
                    </span>
                    <span>{{ $t('Withdrawals') }}</span>
                </inertia-link>

                <inertia-link
                    :href="route('stats.all')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('stats.all') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="signal" />
                    </span>
                    <span>{{ $t('Stats') }}</span>
                </inertia-link>

                <inertia-link
                    :href="route('stats.perGame')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('stats.perGame') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="signal" />
                    </span>
                    <span>{{ $t('Stats Per Game') }}</span>
                </inertia-link>
                <inertia-link
                    :href="route('stats.perDevice')"
                    :data="{
                        from: '01.12.2020',
                        to: now,
                        order: 'username',
                        direction: 'asc'
                    }"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('stats.perDevice') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="signal" />
                    </span>
                    <span>{{ $t('Stats Per Device') }}</span>
                </inertia-link>
                <inertia-link
                    :href="route('stats.accounting')"
                    class="flex items-center p-4 hover:bg-purple-600 hover:text-white text-lg"
                    :class="isRoute('stats.accounting') ? 'text-white bg-purple-600' : ''"
                    @click="isOpen = false">
                    <span class="mr-2 w-icon">
                        <font-awesome-icon icon="file-invoice-dollar" />
                    </span>
                    <span>{{ $t('Accounting') }}</span>
                </inertia-link>
                <div class="fixed bottom-0 w-full border-t flex justify-between text-white bg-purple-700 sm:hidden">
                    <switch-language class="w-full px-5 py-3 hover:text-gray-300 focus:text-gray-300" />
                    <a
                        @click="logout"
                        class="flex bg-purple-800 items-center px-5 py-3 font-medium ml-2 hover:text-gray-300 focus:text-gray-300"
                        :title="$t('Logout')">
                        <font-awesome-icon icon="power-off"/>&nbsp;{{$t('Logout')}}
                    </a>
                </div>
            </aside>
        </nav>
        <main id="pos" class="mx-auto max-w-6xl">
            <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-2 flex items-center relative flex-wrap justify-between">
                <div class="mr-4">
                    <page-header>
                        <portal-target name="page-header" />
                    </page-header>
                </div>
                <div class="my-6 mt-8 flex flex-row justify-end flex-wrap">
                    <portal-target name="actions" />
                </div>
                <flash-messages ref="flash" />
            </div>

            <slot />
            <Notifications></Notifications>
        </main>
    </div>
</template>

<script>
import FlashMessages from '@/Shared/Components/FlashMessages';
import SwitchLanguage from '@/Shared/Components/SwitchLanguage';
import PageHeader from './Components/PageHeader';
import Notifications from "./Components/Notifications";

export default {
    components: {FlashMessages,PageHeader,SwitchLanguage, Notifications},
    data() {
        return {
            timeout: null,
            isOpen: false,
            now: moment().format('DD.MM.YYYY'),
            withdrawalPlayerWithPinAllowed: window.withdrawal_player_with_pin,
        };
    },
    methods: {
        drawer() {
            this.isOpen = !this.isOpen;
        },
        logout() {
            axios.post(route('logout').url()).then(() => {
                window.location = '/';
            })
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            handler(isOpen) {
                if (process.client) {
                    if (isOpen) document.body.style.setProperty("overflow", "hidden");
                    else document.body.style.removeProperty("overflow");
                }
            }
        }
    },
    created() {
        Echo
            .private(`operator-${this.$page.props.user.id}`)
            .listen('WithdrawalRequested', (e) => {
                this.$page.props.flash = [];
                this.$page.props.flash.success = `${e.username} ${this.$t('requested a withdrawal')}`;
                this.$page.props.flash.link = route('players.show', e.playerId);
                this.$refs.flash.openForTimeout();
            })
            .listen('WithdrawalCanceled', (e) => {
                this.$page.props.flash = [];
                this.$page.props.flash.success = `${e.username} ${this.$t('canceled a withdrawal')}`;
                this.$refs.flash.openForTimeout();
            })
        ;

        if (this.$page.props.user.language !== this.$i18n.locale) {
            this.$i18n.locale = this.$page.props.user.language;
        }
    },
    mounted() {
        document.addEventListener("keydown", e => {
            if (e.keyCode == 27 && this.isOpen) this.isOpen = false;
        });
    }
};
</script>
<style lang="scss">
nav {
    -webkit-overflow-scrolling: auto !important;
}

aside {
    a {
        cursor: pointer;
    }
}

#pos {
    padding-top: 64px;
}

a {
    cursor: pointer;
}

.w-icon {
    width: 22px;
}
</style>
